import * as React from 'react';
import { Arrow } from 'rcd-cpp-ui/ui/icon/Arrow';
import { Edit } from 'rcd-cpp-ui/ui/icon/Edit';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import { ValidationError } from 'rcd-cpp-ui/ui/icon/ValidationError';
import { Close } from 'rcd-cpp-ui/ui/icon/Remove';
import './treeSelect.scss';
import { clone, cn } from '../../utils';
import { findAll } from 'rcd-cpp-workspace/utils/array';
import { Pill } from '../Pill/pill';
import { SearchInput } from '../SearchInput/searchInput';
import { useSelector } from '../../utils/useSelector';
import { NodeType } from '../../enums/NodeType';
import { flattenImpliedNodes } from '../../utils/treeUtils';

export type NodeSetting<D = any, S = { [x: string]: any }> = {
  siblingLimit?: number; // default unlimit - within the same parent
  depthLimit?: number; // default unlimit - within the whole tree;
  hideCheckbox?: boolean | ((d: D) => boolean);
  getUniqueKey?: GetUniqueKey<D>; // override default getUniqueKey
  sort?: TreeSortFn<D>;
} & S;

type ExpandedNodes = { [x: string]: boolean | number };
type GetChildren<D> = (d: D) => D[];
type GetUniqueKey<D> = (d: D, parent?: D) => string;
export type TreeSortFn<D> = (n1: Node<D>, n2: Node<D>) => number;
export type RenderDataFn<D, S> = (n: Node<D, S>) => any;
export type IsHiddenFn<D, S> = (
  d: D,
  parent: D[],
  selected: SelectedMap,
  nodeSetting?: {
    setting: S;
    seeOption?: SeeOption;
  }
) => boolean;
export type SeeOption = 'Less' | 'More' | 'All';

export type TreeSelectProps<D, S = any> = {
  selected: (string | D)[];
  implied?: (string | D)[];
  filterImplied?: (impliedNodes: Node<D, S>[]) => Node<D, S>[];
  flattenImplied?: boolean; // When true, splits parent nodes with multiple selected children into separate cards
  renderNodeMenu?: (node: Node<D, NodeSetting<D, S>>, implied?: boolean) => any;
  getData: (() => Promise<D[]>) | D[];
  getChildren: GetChildren<D>;
  getUniqueKey: GetUniqueKey<D>;
  renderData: (n: Node<D, NodeSetting<D, S>>, txt?: string) => any;
  renderDisplayData?: (n: Node<D, NodeSetting<D, S>>, onRemove?: (d: D, parents: D[], setting?: NodeSetting, depth?: number) => void) => any;
  highlightSearchText?: boolean;
  inline?: boolean;
  onChange: (d: D, p: D[], selected: boolean, depth?: number) => void;
  isHidden?: IsHiddenFn<D, S>;
  getClassName?: (
    node: Node<D, NodeSetting<D, S>>,
    type?: TreeType
  ) => string | string[];
  matches?: (d: D, txt?: string) => boolean;
  placeholder?: string;
  disabled?: boolean;
  onBlur?: () => void;
  // settings[0] applies to root node, settings[1] applies nodes at depth = 1, settings[2] applies nodes at depth = 2, etc..
  settings?: NodeSetting<D>[];
  children?: React.ReactNode;
  seeOptions?: SeeOption[];
  noMappingMsg?: string;
  isDisabled?: (node: Node<D>) => boolean;
};

export const TreeSelect = <Data, _S = NodeSetting>({
  getData,
  selected,
  implied,
  filterImplied = n => n,
  flattenImplied = false,
  renderNodeMenu = () => null,
  settings,
  matches,
  isHidden,
  getUniqueKey,
  getChildren,
  highlightSearchText: withHighlight,
  inline,
  seeOptions,
  noMappingMsg,
  renderDisplayData,
  ...props
}: TreeSelectProps<Data, _S>) => {
  const getMountPoint = useSelector(s => s.app?.elFn?.getMountPoint);
  const el = getMountPoint ? getMountPoint() : document;
  const ref = React.useRef(null);
  const [seeOption, setSeeOption] = React.useState<SeeOption>(null);
  const [data, setData] = React.useState([]);
  const isEmptySelection = Array.isArray(selected) && selected.length === 0;
  const { editMode: isEditMode } = useSelector(s => s.view);
  const [active, setActive] = React.useState(
    !!inline || (isEditMode && isEmptySelection)
  );
  const [expandedNodes, setExpandedNodes] = React.useState({} as ExpandedNodes);
  const [text, setText] = React.useState('');
  const { placeholder, onBlur, onChange, disabled, getClassName } = props;
  const selectedMap = getSelectedMap(selected, {
    getUniqueKey,
    getChildren,
    settings
  });
  const impliedMap = getSelectedMap(implied, {
    getUniqueKey,
    getChildren,
    settings
  });

  React.useEffect(() => {
    if (!active && seeOptions?.length) setSeeOption(null);
  }, [active]);

  React.useEffect(() => {
    if (isEditMode && isEmptySelection) {
      setActive(true);
    }
  }, [isEditMode, selected]);

  React.useEffect(() => {
    if (typeof getData == 'function') {
      if (!data?.length) getData().then(d => setData(d));
    } else if (Array.isArray(getData)) {
      setData(getData);
    }
  }, [getData]);

  React.useEffect(() => {
    if (isEditMode) {
      return;
    }
    const handleClickOutside = event => {
      if (!ref?.current?.contains(event.target)) {
        if (!inline) setActive(false);
        if (onBlur) onBlur();
        setSeeOption(null);
      }
    };
    if (active && !inline) {
      el.addEventListener('mousedown', handleClickOutside);
      return () => {
        el.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [ref, active, inline, isEditMode]);

  const nodes = toTreeNode(data, selectedMap, seeOption, {
    getChildren,
    getUniqueKey,
    isHidden,
    settings
  });

  const impliedNodesRaw = filterImplied(
    toTreeNode(data, impliedMap, seeOption, {
      getChildren,
      getUniqueKey,
      isHidden: (d, p, s, ns) => {
        const k = getUniqueKey(d);
        if (selectedMap[k]) return true;
        return isHidden(d, p, s, ns);
      },
      settings
    }).filter(n => n.isSelected || n.numOfDecendentsSelected > 0)
  );

  const impliedNodes = flattenImplied
    ? flattenImpliedNodes(impliedNodesRaw)
    : impliedNodesRaw;

  React.useEffect(() => {
    const txt = text.trim();
    const expanded = {};
    Object.entries(expandedNodes).forEach(([k, v]) => {
      if (typeof v == 'boolean') expanded[k] = v;
    });
    if (!txt) {
      if (active) {
        const defaultExpanded = { ...selectedMap };
        traverse(nodes, n => {
          if (!defaultExpanded[n.key]) {
            const expanded = traverse(n.children, _ => {
              if (selectedMap[_.key]) return true;
            });
            if (expanded) {
              defaultExpanded[n.key] = true;
            }
          }
        });
        setExpandedNodes(defaultExpanded);
      } else {
        setExpandedNodes(expanded);
      }
    } else {
      const matched = {};
      traverse(nodes, n => {
        if (!n.isHidden && matches(n.data, txt)) {
          matched[n.key] = true;
        }
      });
      traverse(nodes, n => {
        if (!n.isLeaf && n.children?.length) {
          const m = traverse(n.children, c => {
            if (matched[c.key]) return true;
          });
          if (m) expanded[n.key] = 1;
        }
      });
      setExpandedNodes(expanded);
    }
  }, [text, active]);
  const displayNodes = nodes?.filter(
    n => n.isSelected || n.numOfDecendentsSelected > 0
  );

  const handleRemove = (d: Data, p: Data[], s?: NodeSetting, depth?: number) => {
    setActive(true);
    if (onChange) onChange(d, p, false, depth);
  };

  return (
    <div
      className={cn('tree-select', {
        'tree-select-active': active
      })}
      ref={ref}
    >
      {!!displayNodes.length &&
        displayNodes.map(node => (
          <div className="tree-select-card-container" key={node.key}>
            <div
              key={node.key}
              className={cn(
                'tree-select-display card',
                getClassName
                  ? fixClassNames(
                      getClassName(node, 'display'),
                      'tree-select-display'
                    )
                  : null
              )}
            >
              <Tree<Data>
                {...props}
                isVisible={n => n.isSelected || n.numOfDecendentsSelected > 0}
                onRemove={
                  (renderDisplayData || disabled)
                    ? null
                    : handleRemove
                }
                nodes={[node]}
                type="display"
                renderData={renderDisplayData
                  ? (n, _txt) => renderDisplayData(n, disabled ? null : handleRemove)
                  : props.renderData
                }
              />
              {!active && !disabled && (
                <Icon onClick={() => setActive(true)}>
                  <Edit />
                </Icon>
              )}
            </div>
            {renderNodeMenu(node)}
          </div>
        ))}

      {!props.disabled && (
        <div
          className={cn('tree-select-edit', {
            card: active || selected?.length > 0
          })}
        >
          <span className="input-group">
            <input
              className="form-control"
              placeholder={placeholder}
              value={text}
              onFocus={() => setActive(true)}
              onChange={e => setText(e.target.value)}
            />
            <SearchInput />
          </span>
          {active && (
            <SeeLessMoreAll
              nodes={nodes}
              noMappingMsg={noMappingMsg}
              selected={seeOption}
              seeOptions={seeOptions}
              setSeeOption={setSeeOption}
            />
          )}
          {active && (
            <div className="scrollable">
              <Tree<Data>
                {...props}
                nodes={nodes}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                renderData={d => {
                  const el = props.renderData(d, text);
                  if (typeof el == 'string') {
                    return (
                      <span>{withHighlight ? addHighlight(el, text) : el}</span>
                    );
                  }
                  return el;
                }}
                isVisible={({ data, isHidden, parents, children }) => {
                  if (isHidden) return false;
                  const txt = text?.trim();
                  if (matches && txt) {
                    let matched = matches(data, txt);
                    if (!matched && parents?.length) {
                      for (const p of parents) {
                        if (matches(p, txt)) {
                          matched = true;
                          break;
                        }
                      }
                    }
                    if (!matched) {
                      matched = traverse(children, n => {
                        if (!n.isHidden && matches(n.data, txt)) return true;
                      });
                    }
                    return matched;
                  }
                  //if (isSelected || numOfDecendentsSelected > 0) return true;
                  return true;
                }}
              />
              {props.children}
            </div>
          )}
        </div>
      )}
      {!disabled &&
        impliedNodes?.map((node, index) => (
          <div
            className="tree-select-card-container"
            key={`${node.key}-${index}`}
          >
            <div
              key={`${node.key}-${index}`}
              className={cn(
                'tree-select-display tree-select-implied card',
                getClassName
                  ? fixClassNames(
                      getClassName(node, 'implied'),
                      'tree-select-display'
                    )
                  : null
              )}
            >
              <Tree<Data>
                {...props}
                isVisible={n => n.isSelected || n.numOfDecendentsSelected > 0}
                nodes={[node]}
                disabled={true}
                type="implied"
                renderData={renderDisplayData ? (n, _txt) => renderDisplayData(n) : props.renderData}
              />
              <Pill className="pill-implied" name="Implied" />
            </div>
            {renderNodeMenu(node, true)}
          </div>
        ))}
    </div>
  );
};

type SeeLessMoreAllProps = {
  seeOptions: SeeOption[];
  setSeeOption: (opt: SeeOption) => void;
  selected: SeeOption;
  nodes: Node<any, any>[];
  noMappingMsg: string;
};
const SeeLessMoreAll = ({
  seeOptions,
  setSeeOption,
  selected,
  noMappingMsg,
  nodes
}: SeeLessMoreAllProps) => {
  if (!seeOptions?.length) return null;
  const s = selected || seeOptions[0];
  const idx = seeOptions.indexOf(s);
  const nextIdx = (idx + 1) % seeOptions.length;
  const next = seeOptions[nextIdx];
  let msg = null;
  if (
    ['More', 'All'].includes(next) &&
    nodes.filter(n => !n.isHidden).length == 0
  ) {
    msg = noMappingMsg;
  }

  return (
    <>
      <div
        className="see-more"
        onClick={() => setSeeOption(nextIdx == 0 ? null : next)}
      >
        See {next}
      </div>
      {!!msg && <div className="see-more-msg">{msg}</div>}
    </>
  );
};

type SelectedMap = { [x: string]: boolean };
type TreeType = 'edit' | 'display' | 'implied';

interface TreeProps<Data> extends Omit<
  TreeSelectProps<Data>,
  | 'selected'
  | 'getData'
  | 'data'
  | 'isVisible'
  | 'getChildren'
  | 'getUniqueKey'
  | 'isHidden'
> {
  nodes: Node<Data>[];
  type?: TreeType;
  onRemove?: (
    d: Data,
    parents: Data[],
    setting?: NodeSetting,
    depth?: number
  ) => void;
  expandedNodes?: ExpandedNodes;
  setExpandedNodes?: (val: ExpandedNodes) => void;
  isVisible: (node: Node<Data>) => boolean;
}

const Tree = <Data,>({ nodes, ...props }: TreeProps<Data>) => {
  if (!nodes?.length) return null;
  const visibleNodes = nodes.filter(n => props.isVisible(n));
  if (!visibleNodes.length) return null;
  return (
    <ul className={'tree-select-group'}>
      {visibleNodes.map(node => {
        return <TreeNode key={node.key} {...props} node={node} />;
      })}
    </ul>
  );
};

interface TreeNodeProps<Data> extends Omit<TreeProps<Data>, 'nodes'> {
  node: Node<Data>;
}

const TreeNode = <Data,>({ node, ...props }: TreeNodeProps<Data>) => {
  const {
    renderData,
    onChange,
    onRemove,
    expandedNodes,
    getClassName,
    setExpandedNodes,
    isDisabled
  } = props;
  const {
    key,
    parents,
    data,
    numOfDecendentsSelected,
    hideCheckbox,
    isLeaf,
    isHidden,
    isSelected,
    children
  } = node;
  const expanded = expandedNodes ? expandedNodes[key] : false;
  const setExpanded = (v: boolean) => {
    if (setExpandedNodes) setExpandedNodes({ ...expandedNodes, [key]: v });
  };
  const toggleNode = (checked: boolean) => {
    if (onChange) onChange(clone(data), clone(parents), checked, node.depth);
    if (checked) setExpanded(true);
  };
  if (!isSelected && numOfDecendentsSelected == 0) {
    if (isHidden) return null;
    if (hideCheckbox) {
      const visibleChildren = children?.filter(c => !c.isHidden);
      if (!visibleChildren?.length) {
        return null;
      }
    }
  }
  const className = getClassName
    ? getClassName({ ...node, children: [] }, props.type)
    : null;
  const classNames = (
    Array.isArray(className) ? className : [className]
  ).filter(x => x);
  if (isLeaf) classNames.push('--leaf');
  if (!expanded && !props.type) classNames.push('--collapsed');
  const showErrorIcon = classNames.includes('--error');
  const showChildren = !!props.type || (!!children?.length && !!expanded);
  const disabled = isDisabled && isDisabled(node);
  if (disabled) classNames.push('--disabled');
  return (
    <li
      className={cn(
        'tree-select-item',
        fixClassNames(classNames, 'tree-select-item')
      )}
    >
      <div>
        <div>
          {!props.type && !hideCheckbox && (
            <input
              type="checkbox"
              checked={!!isSelected}
              disabled={disabled}
              onChange={() => {
                return toggleNode(!isSelected);
              }}
            />
          )}
          {!props.type && (
            <Icon onClick={() => setExpanded(!expanded)}>
              <Arrow type="solid" direction={expanded ? 'down' : 'right'} />
            </Icon>
          )}
          {showErrorIcon && <ValidationError width={12} height={12} />}
        </div>
        <span
          onClick={() => {
            if (props.type || (disabled && isLeaf)) return;
            if (!isLeaf) {
              setExpanded(!expanded);
            } else if (onChange) {
              toggleNode(!isSelected);
            }
          }}
        >
          {renderData(node)}
          {!!onRemove && isSelected && !disabled && (
            <Icon
              onClick={() => {
                onRemove(clone(data), clone(parents), node.setting, node.depth);
              }}
            >
              <Close />
            </Icon>
          )}
        </span>
      </div>
      {showChildren && <Tree {...props} nodes={children} />}
    </li>
  );
};

const _traverse = <T, R>(
  tree: T,
  getChildren: GetChildren<T>,
  fn: (n: T, depth?: number, p?: T) => R,
  depth: number = 0,
  parent: T = null
): R => {
  if (tree) {
    const r = fn(tree, depth, parent);
    if (r !== undefined) return r;
  }
  const children = getChildren(tree);
  if (children?.length) {
    for (const child of children) {
      const r = _traverse(child, getChildren, fn, depth + 1, tree);
      if (r !== undefined) return r;
    }
  }
};

const getSelectedMap = <T,>(
  selected: T | T[] | string[] | string,
  {
    getChildren,
    getUniqueKey,
    settings
  }: Pick<TreeSelectProps<T>, 'getChildren' | 'getUniqueKey' | 'settings'>
) => {
  const selectedMap: SelectedMap = {};
  if (!selected) return selectedMap;
  const arr = Array.isArray(selected) ? selected : [selected];
  arr
    .filter(x => x)
    .forEach(t => {
      if (typeof t == 'string') {
        selectedMap[t] = true;
      } else {
        _traverse(
          t,
          getChildren,
          (n, depth, p) => {
            const setting = settings[depth];
            const _getUniqueKey = setting.getUniqueKey || getUniqueKey;
            const k = _getUniqueKey(n, p);
            selectedMap[k] = true;
          },
          0,
          t
        );
      }
    });
  return selectedMap;
};

export interface Node<D, S = NodeSetting> {
  key: string;
  data: D;
  parents: D[];
  isSelected?: boolean;
  isHidden?: boolean;
  isLeaf?: boolean;
  hideCheckbox?: boolean;
  numOfChildSelected?: number;
  numOfDecendentsSelected?: number;
  depth: number;
  setting: S;
  children: Node<D>[];
}

export const toTreeNode = <D,>(
  rawDataArr: D[],
  selectedMap: SelectedMap,
  seeOption: SeeOption,
  props: Pick<
    TreeSelectProps<D>,
    'getChildren' | 'getUniqueKey' | 'settings' | 'isHidden'
  >,
  parents: D[] = [],
  depth: number = 0,
  selectedCountsByDepth: number[] = []
): Node<D, any>[] => {
  if (!Array.isArray(rawDataArr)) return [];
  const { getChildren, getUniqueKey, settings } = props;
  const setting = settings[depth];
  if (!setting) return [];
  const _getUniqueKey = setting.getUniqueKey || getUniqueKey;
  const nodes = rawDataArr.reduce((nodes, data) => {
    const childSetting = settings[depth + 1];
    const children = toTreeNode(
      getChildren(data),
      selectedMap,
      seeOption,
      props,
      [...parents, data],
      depth + 1,
      selectedCountsByDepth
    );
    const parent = parents[parents.length - 1];
    const key = _getUniqueKey(data, parent);
    const isSelected = selectedMap[key];
    const isHidden = props.isHidden
      ? props.isHidden(data, parents, selectedMap, { setting, seeOption })
      : undefined;
    const isLeaf = childSetting == null || !children?.length;
    const hideCheckbox =
      typeof setting.hideCheckbox == 'function'
        ? setting.hideCheckbox(data)
        : setting.hideCheckbox;
    let numOfChildSelected = 0;
    let numOfDecendentsSelected = 0;
    traverse(
      children,
      (n, d) => {
        if (n.isSelected) {
          numOfDecendentsSelected++;
          if (d == depth) numOfChildSelected++;
        }
      },
      depth
    );
    if (isSelected) {
      selectedCountsByDepth[depth] = (selectedCountsByDepth[depth] || 0) + 1;
    }

    nodes.push({
      key,
      data,
      isSelected,
      isHidden,
      isLeaf,
      hideCheckbox,
      numOfChildSelected,
      numOfDecendentsSelected,
      parents,
      children,
      setting,
      depth
    });
    return nodes;
  }, [] as Node<D>[]);

  if (depth == 0) {
    settings.forEach((setting, d) => {
      const parentNodes = getNodesInDepth(nodes, d - 1);
      if (setting.sort) {
        parentNodes.forEach(pn => {
          if (pn.children) pn.children.sort(setting.sort);
        });
      }
      if (setting.siblingLimit) {
        parentNodes.forEach(pn => {
          const selectedNodes = findAll(pn.children, { isSelected: true });
          if (selectedNodes.length >= setting.siblingLimit) {
            pn.children.forEach(c => {
              if (!c.isSelected) c.isHidden = true;
            });
          }
        });
      }
      if (setting.depthLimit) {
        const allChildren = parentNodes.reduce((result, n) => {
          if (n.children?.length) return result.concat(n.children);
          return result;
        }, []);
        if (setting.hideCheckbox && !setting.field) {
          const hasSelectedChild = (p: Node<any>) => {
            if (!p?.children?.length) return false;
            for (const c of p.children) {
              if (selectedMap[c.key]) return true;
            }
          };
          if (Object.keys(selectedMap).length) {
            const hasChildSelected: string[] = [];
            for (var p of allChildren) {
              if (hasSelectedChild(p)) hasChildSelected.push(p.key);
            }
            if (hasChildSelected.length >= setting.depthLimit) {
              for (var p of allChildren) {
                if (!hasChildSelected.includes(p.key)) p.isHidden = true;
              }
            }
          }
        } else {
          const selectedNodes = findAll(allChildren, { isSelected: true });
          if (selectedNodes.length >= setting.depthLimit) {
            allChildren.forEach(c => {
              if (!c.isSelected) c.isHidden = true;
            });
          }
        }
      }
    });
    traverseDepthFirst(nodes, (n, d) => {
      if (!n.isSelected && n.numOfDecendentsSelected == 0 && !n.isLeaf) {
        const visibleChildren = n.children?.filter(c => !c.isHidden);
        if (!visibleChildren?.length) {
          n.isHidden = true;
        }
      }
    });
  }
  return nodes;
};

const getNodesInDepth = <D,>(nodes: Node<D>[], level: number): Node<D>[] => {
  if (!nodes?.length) return [];
  if (level == -1) return [{ children: nodes } as any];
  if (level == 0) return nodes;
  if (level > 0) {
    return nodes.reduce<Node<D>[]>((result, n) => {
      const c = getNodesInDepth(n.children, level - 1);
      if (c?.length) {
        return result.concat(c);
      }
      return result;
    }, []);
  }
  return [];
};

export const traverse = <D, R>(
  nodes: Node<D>[],
  fn: (n: Node<D>, depth?: number) => R,
  depth: number = 0
): R => {
  if (Array.isArray(nodes)) {
    for (const node of nodes) {
      let r = fn(node, depth);
      if (r !== undefined) return r;
      r = traverse(node.children, fn, depth + 1);
      if (r !== undefined) return r;
    }
  }
};

const traverseDepthFirst = <D, R>(
  nodes: Node<D>[],
  fn: (n: Node<D>, depth?: number) => R,
  depth: number = 0
): R => {
  if (Array.isArray(nodes)) {
    for (const node of nodes) {
      let r = traverseDepthFirst(node.children, fn, depth + 1);
      if (r !== undefined) return r;
      r = fn(node, depth);
      if (r !== undefined) return r;
    }
  }
};

const fixClassNames = (
  classNames: string | string[],
  prefix: string
): string[] => {
  if (typeof classNames == 'string') classNames = [classNames];
  return classNames.map(c => {
    if (c.startsWith('--')) return `${prefix}${c}`;
    return c;
  });
};

const addHighlight = (str: string, hlTxt: string) => {
  if (!!hlTxt && !!str) {
    if (str.toLowerCase() == hlTxt.toLowerCase()) return <b>{str}</b>;
    const split = str.toLowerCase().split(hlTxt.toLowerCase());
    if (split.length > 1) {
      const result = [];
      const hlTxtLength = hlTxt.length;
      let curIdx = 0;
      split.forEach((s, i) => {
        result.push(str.substring(curIdx, s.length + curIdx));
        curIdx += s.length;
        if (i < split.length - 1) {
          result.push(
            <b key={i}>{str.substring(curIdx, curIdx + hlTxtLength)}</b>
          );
          curIdx += hlTxtLength;
        }
      });
      return result;
    }
  }
  return str;
};
